(() => {
  'use strict';

  if (window.TSSProjectProgressInitialised) {
    return;
  }

  window.TSSProjectProgressInitialised = true;

  /*
   * Thresholds are stored in cents:
   * A$500    = 50,000 cents
   * A$1,500  = 150,000 cents
   * A$4,000  = 400,000 cents
   * A$7,000  = 700,000 cents
   */
  const tiers = [
    { threshold: 50000, discount: 5 },
    { threshold: 150000, discount: 10 },
    { threshold: 400000, discount: 15 },
    { threshold: 700000, discount: 20 }
  ];

  let updateTimer = null;

  function formatAud(cents) {
    const amount = Number(cents || 0) / 100;

    return `$${amount.toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  }

  function calculateState(subtotal) {
    if (subtotal >= 700000) {
      return {
        message:
          '<strong>20% project pricing unlocked.</strong> ' +
          'This is the highest project-pricing tier.',
        progress: 100,
        nextDiscount: null
      };
    }

    const nextTier = tiers.find((tier) => subtotal < tier.threshold);

    const unlockedTiers = tiers.filter(
      (tier) => subtotal >= tier.threshold
    );

    const currentTier = unlockedTiers.length
      ? unlockedTiers[unlockedTiers.length - 1]
      : null;

    const previousThreshold = currentTier
      ? currentTier.threshold
      : 0;

    const remaining = nextTier.threshold - subtotal;

    const tierRange =
      nextTier.threshold - previousThreshold;

    const progressWithinTier =
      tierRange > 0
        ? ((subtotal - previousThreshold) / tierRange) * 100
        : 100;

    const progress = Math.max(
      0,
      Math.min(100, progressWithinTier)
    );

    let message;

    if (currentTier) {
      message =
        `<strong>${currentTier.discount}% project pricing unlocked.</strong> ` +
        `Add <strong>${formatAud(remaining)}</strong> more ` +
        `to unlock ${nextTier.discount}%.`;
    } else {
      message =
        `Add <strong>${formatAud(remaining)}</strong> more ` +
        `to unlock 5% project pricing.`;
    }

    return {
      message,
      progress,
      nextDiscount: nextTier.discount
    };
  }

  function renderCartProgress(cart) {
    const elements = document.querySelectorAll(
      '[data-tss-project-progress]'
    );

    if (!elements.length) {
      return;
    }

    const subtotal = Number(
      cart.items_subtotal_price || 0
    );

    elements.forEach((container) => {
      /*
       * Hide the box when the cart is empty.
       * Change this to false if you want it visible
       * in an empty cart.
       */
      if (!cart.item_count) {
        container.hidden = true;
        return;
      }

      const state = calculateState(subtotal);

      const messageElement = container.querySelector(
        '[data-tss-project-message]'
      );

      const subtotalElement = container.querySelector(
        '[data-tss-project-subtotal]'
      );

      const progressTrack = container.querySelector(
        '[data-tss-project-track]'
      );

      const progressBar = container.querySelector(
        '[data-tss-project-bar]'
      );

      container.hidden = false;

      if (messageElement) {
        messageElement.innerHTML = state.message;
      }

      if (subtotalElement) {
        subtotalElement.textContent =
          `Project pricing subtotal: ${formatAud(subtotal).replace('A$', '$')} incl. GST`;
      }

      if (progressBar) {
        progressBar.style.width = `${state.progress}%`;
      }

      if (progressTrack) {
        progressTrack.setAttribute(
          'aria-valuenow',
          String(Math.round(state.progress))
        );

        if (state.nextDiscount) {
          progressTrack.setAttribute(
            'aria-label',
            `Progress towards ${state.nextDiscount}% project pricing`
          );
        } else {
          progressTrack.setAttribute(
            'aria-label',
            'Highest project-pricing tier unlocked'
          );
        }
      }
    });
  }

  async function retrieveCart() {
    const root =
      window.Shopify &&
      window.Shopify.routes &&
      window.Shopify.routes.root
        ? window.Shopify.routes.root
        : '/';

    const response = await fetch(`${root}cart.js`, {
      method: 'GET',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error(
        `Unable to retrieve cart: ${response.status}`
      );
    }

    return response.json();
  }

  async function updateProgress() {
    try {
      const cart = await retrieveCart();
      renderCartProgress(cart);
    } catch (error) {
      console.warn(
        'Project-pricing progress could not be updated.',
        error
      );
    }
  }

  function queueUpdate(delay = 100) {
    window.clearTimeout(updateTimer);

    updateTimer = window.setTimeout(
      updateProgress,
      delay
    );
  }

  /*
   * Listen for Shopify's standard 2026 cart event.
   */
  document.addEventListener(
    'shopify:cart:lines-update',
    () => queueUpdate(0)
  );

  /*
   * Common events used by existing Shopify themes.
   */
  [
    'cart:updated',
    'cart:refresh',
    'cart:change',
    'theme:cart:change'
  ].forEach((eventName) => {
    document.addEventListener(
      eventName,
      () => queueUpdate(0)
    );
  });

  /*
   * Detect themes that update the cart using fetch().
   */
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (...argumentsList) => {
    const response = await originalFetch(...argumentsList);

    try {
      const request = argumentsList[0];

      const url =
        typeof request === 'string'
          ? request
          : request && request.url
            ? request.url
            : '';

      if (
        /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(
          url
        )
      ) {
        queueUpdate(150);
      }
    } catch (error) {
      // Do not interfere with the store's cart request.
    }

    return response;
  };

  /*
   * Detect older themes that update the cart using XMLHttpRequest.
   */
  const originalOpen =
    window.XMLHttpRequest.prototype.open;

  const originalSend =
    window.XMLHttpRequest.prototype.send;

  window.XMLHttpRequest.prototype.open = function (
    method,
    url,
    ...remainingArguments
  ) {
    this.tssCartRequestUrl =
      typeof url === 'string' ? url : '';

    return originalOpen.call(
      this,
      method,
      url,
      ...remainingArguments
    );
  };

  window.XMLHttpRequest.prototype.send = function (
    ...argumentsList
  ) {
    if (
      /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(
        this.tssCartRequestUrl || ''
      )
    ) {
      this.addEventListener(
        'loadend',
        () => queueUpdate(150),
        { once: true }
      );
    }

    return originalSend.apply(this, argumentsList);
  };

  /*
   * Recalculate after Shopify reloads a section in
   * the theme editor or storefront.
   */
  document.addEventListener(
    'shopify:section:load',
    () => queueUpdate(0)
  );

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      updateProgress,
      { once: true }
    );
  } else {
    updateProgress();
  }

  window.TSSProjectProgress = {
    update: updateProgress
  };
})();